import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import type { ComponentProps } from "react";

import {
  getPageTitle,
  getPageTitleHref,
  shouldShowDefaultPageTitle,
} from "@src/features/app/top-nav/pageTitle";
import BuilderHeaderBackButtonV2 from "@src/features/app/top-nav/BuilderHeaderBackButtonV2";
import { useDefaultPageTitleHidden } from "@src/features/app/top-nav/useDefaultPageTitleHidden";
import {
  getBuilderBackHref,
  getBuilderDomain,
  getStorageKey,
  isBuilderView,
  isTrackableListView,
  getBuilderInternalBackHref,
} from "@src/features/app/top-nav/builderNav";
import { cn } from "@src/lib/utils/cn";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { Ff } from "@src/types.generated";
import { useActiveLibraryItemMainTab } from "@src/features/libraryItem/contexts/libraryItemMainTabStore";
import {
  BREADCRUMB_LINK_CLASS,
  PAGE_TITLE_CLASS,
} from "@src/features/app/top-nav/PageContentHeaderZone";
import { useHasChildren } from "@src/features/app/top-nav/useHasChildren";

type LinkHref = ComponentProps<typeof Link>["href"];

/**
 * The page header zone while Ff.LibraryItemOverviewTab is on: the section
 * tabs get a real centred column and the full-screen editor hides the
 * shell's back arrow. PageContentHeaderZone is the previous zone.
 */
const PageContentHeaderZoneV2: React.FC = () => {
  const router = useRouter();
  const [titleOverrideRef, hasPageTitleOverride] = useHasChildren();
  const [mainTabsRef, hasMainTabs] = useHasChildren();
  const [rememberedBuilderBackHref, setRememberedBuilderBackHref] =
    React.useState<LinkHref | null>(null);
  const auditsEnabled = useFeatureFlag(Ff.Audits);
  const taskLibraryItemTypeEnabled = useFeatureFlag(Ff.TaskLibraryItemType);
  const operationsBundle = !auditsEnabled && !taskLibraryItemTypeEnabled;
  const isBuilder = isBuilderView(router.pathname, router.query);
  const isFullScreenEditor = useActiveLibraryItemMainTab() === "build";
  const pageTitle = getPageTitle(
    router.pathname,
    router.query,
    operationsBundle,
  );
  const pageTitleHref = getPageTitleHref(router.pathname, router.query);
  const builderBackFallbackHref = isBuilder
    ? getBuilderBackHref(router.pathname, router.query)
    : null;
  const builderInternalBackHref = isBuilder
    ? getBuilderInternalBackHref(router.pathname, router.query)
    : null;
  const builderBackHref =
    builderInternalBackHref ||
    rememberedBuilderBackHref ||
    builderBackFallbackHref;
  const titleHiddenByPage = useDefaultPageTitleHidden();
  const showDefaultTitle =
    !isBuilder &&
    !hasPageTitleOverride &&
    !titleHiddenByPage &&
    shouldShowDefaultPageTitle(router.pathname);

  React.useEffect(() => {
    if (!router.isReady) return;

    const domain = getBuilderDomain(router.pathname);

    if (isTrackableListView(router.pathname, router.query) && domain) {
      window.sessionStorage.setItem(getStorageKey(domain), router.asPath);
      setRememberedBuilderBackHref(null);
      return;
    }

    if (isBuilder && domain) {
      const storedBackHref = window.sessionStorage.getItem(
        getStorageKey(domain),
      );
      setRememberedBuilderBackHref(
        storedBackHref ? (storedBackHref as LinkHref) : null,
      );
    }
  }, [isBuilder, router.asPath, router.isReady, router.pathname, router.query]);

  if (router.pathname === "/" && !isBuilder && !builderBackHref) {
    return null;
  }

  if (router.pathname.startsWith("/library/folders")) {
    return null;
  }

  if (router.pathname.startsWith("/settings")) {
    return null;
  }

  if (router.pathname.startsWith("/guest-feedback/")) {
    return null;
  }

  if (
    router.pathname.startsWith("/messages") ||
    router.pathname.startsWith("/newsfeed")
  ) {
    return null;
  }

  return (
    <div
      className={cn(
        "@container/page-header shrink-0 px-2 md:px-4",
        isBuilder ? "border-b border-border-primary py-2" : "py-3",
      )}
    >
      <div
        className={cn(
          "min-w-0 items-center",
          // Three columns once the section tabs mount. The outer tracks are
          // equal while there's room, so the middle lands on the header's
          // own midpoint; the actions track is floored at its content, so a
          // cramped header truncates the title and slides the tabs off-axis
          // instead of letting the two overlap — which is what an absolutely
          // placed group did.
          // The wider gap keeps a truncated title from crowding the tabs.
          // A narrow header (the agent panel open on a laptop) first drops
          // the tab labels to icons, then moves the tabs to their own row.
          hasMainTabs
            ? "grid grid-cols-[minmax(0,1fr)_auto_minmax(max-content,1fr)] gap-8 @max-[36rem]/page-header:grid-cols-[minmax(0,1fr)_max-content] @max-[36rem]/page-header:gap-x-4 @max-[36rem]/page-header:gap-y-2"
            : "flex justify-between gap-4",
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex min-w-0 items-center gap-2">
            <div
              id="top-nav-page-title-leading"
              className="-mr-2 -ml-1.5 flex items-center gap-1.5 empty:hidden"
            >
              {/* The full-screen editor carries its own "Back to details" in
                  the title slot — two back arrows side by side read as a bug. */}
              {isBuilder && builderBackHref && !isFullScreenEditor ? (
                <BuilderHeaderBackButtonV2
                  href={builderBackHref}
                  replace={builderInternalBackHref != null}
                />
              ) : null}
            </div>
            {!isBuilder && (
              <div
                id="top-nav-page-title-override"
                ref={titleOverrideRef}
                className="flex min-w-0 items-center empty:hidden"
              />
            )}
            {showDefaultTitle &&
              (pageTitleHref ? (
                <Link href={pageTitleHref} className={BREADCRUMB_LINK_CLASS}>
                  {pageTitle}
                </Link>
              ) : (
                <h1 className={PAGE_TITLE_CLASS}>{pageTitle}</h1>
              ))}
            <div
              id="top-nav-page-title-extra"
              className="flex min-w-0 items-center gap-1.5"
            />
          </div>
          <div id="top-nav-page-subtitle" className="min-w-0 empty:hidden" />
        </div>
        <div
          id="top-nav-main-tabs"
          ref={mainTabsRef}
          className="flex min-w-0 items-center justify-center empty:hidden @max-[36rem]/page-header:col-span-2 @max-[36rem]/page-header:row-start-2 @max-[36rem]/page-header:justify-start"
        />
        <div
          id="top-nav-actions"
          className="flex shrink-0 items-center justify-end gap-1.5 empty:hidden"
        />
      </div>
    </div>
  );
};

export default PageContentHeaderZoneV2;
